import React from 'react';
import { FiLock, FiEye, FiEyeOff, FiCheck, FiX } from 'react-icons/fi';
import logger from '../../utils/logging/logger';
import useSimplePasswordChangeModal from './useSimplePasswordChangeModal';
import {
  ModalOverlay, ModalContent, CloseButton, Title, Subtitle, Form, FormGroup, Label,
  InputWrapper, Icon, Input, PasswordToggle, Button, ErrorMessage, SuccessMessage,
  LoadingSpinner, RequirementsList, RequirementItem, RequirementIcon
} from './SimplePasswordChangeModal.styles';

function SimplePasswordChangeModal({ isOpen, onClose, onSuccess }) {
  const {
    formData, showPasswords, loading, error, success, passwordRequirements, isPasswordValid,
    passwordsMatch, isDifferentFromCurrent, handleChange, togglePasswordVisibility, handleSubmit
  } = useSimplePasswordChangeModal({ onSuccess });

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            logger.debug('Close button clicked', { onClose: typeof onClose });
            onClose();
          }}
          aria-label="Close modal"
        >
          <FiX size={20} />
        </CloseButton>

        <Title>Change Your Password</Title>
        <Subtitle>
          For security reasons, you must change your password before continuing.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Current Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                placeholder="Enter current password"
                value={formData.currentPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label>New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
            {formData.newPassword && (
              <RequirementsList>
                <RequirementItem valid={passwordRequirements.length}>
                  <RequirementIcon valid={passwordRequirements.length}>
                    {passwordRequirements.length ? '✓' : '○'}
                  </RequirementIcon>
                  At least 10 characters
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.digit}>
                  <RequirementIcon valid={passwordRequirements.digit}>
                    {passwordRequirements.digit ? '✓' : '○'}
                  </RequirementIcon>
                  At least 1 digit
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.specialChar}>
                  <RequirementIcon valid={passwordRequirements.specialChar}>
                    {passwordRequirements.specialChar ? '✓' : '○'}
                  </RequirementIcon>
                  At least 1 special character
                </RequirementItem>
                <RequirementItem valid={isDifferentFromCurrent}>
                  <RequirementIcon valid={isDifferentFromCurrent}>
                    {isDifferentFromCurrent ? '✓' : '○'}
                  </RequirementIcon>
                  Different from current password
                </RequirementItem>
              </RequirementsList>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Confirm New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          {success && <SuccessMessage role="alert">{success}</SuccessMessage>}

          <Button
            type="submit"
            disabled={loading || !isPasswordValid || !passwordsMatch || !formData.currentPassword || !isDifferentFromCurrent}
          >
            {loading ? <LoadingSpinner /> : <FiCheck size={16} />}
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
}

export default SimplePasswordChangeModal;
